import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowsClockwise, MagnifyingGlass, PencilSimple, X, ChartBar } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Iter-63.1 — Admin Prospect Engagement page.
 *
 * Lists every prospect-share preview token minted under
 * `landing_preview_tokens`, joined with the first-party engagement counters
 * the public share surface emits. Supports filtering by viewed status,
 * prospect firm, and free-text search across name / firm / slug / label.
 *
 * Reuses the existing SharePreview minting flow from Landing Pages — this
 * page annotates each share with buyer-facing metadata (prospect name,
 * firm, contact, notes, sent date) so the admin knows WHO they sent a
 * preview to and whether the prospect actually opened it.
 */
export default function AdminProspectEngagement() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, page_size: 25, has_next: false });
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [viewed, setViewed] = useState("all");
  const [firm, setFirm] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);       // token_id being annotated
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const isInternal = user?.org_type === "internal";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("page_size", "25");
      if (q) params.set("q", q);
      if (firm) params.set("firm", firm);
      if (viewed !== "all") params.set("viewed", viewed);
      const { data } = await api.get(`/admin/prospect-engagement?${params.toString()}`);
      setRows(data.shares || []);
      setPagination(data.pagination || { total: 0, page: 1, page_size: 25, has_next: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load prospect engagement");
    } finally {
      setLoading(false);
    }
  }, [page, q, firm, viewed]);

  useEffect(() => { setPage(1); }, [q, firm, viewed]);
  useEffect(() => { if (isInternal) load(); }, [load, isInternal]);

  // Iter-63.1.7 — lightweight auto-refresh. Fires `load()` every 15 s while
  // the page is mounted so operators watching a live smoke test see the
  // counters advance without clicking Refresh. Uses a ref to the latest
  // `load` so the interval NEVER resets on filter/search/page changes
  // (which would reset the countdown from zero on every keystroke), and
  // is torn down cleanly on unmount to avoid duplicate timers.
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);
  useEffect(() => {
    if (!isInternal) return undefined;
    const id = setInterval(() => { loadRef.current(); }, 15000);
    return () => clearInterval(id);
  }, [isInternal]);

  // Debounce free-text search — same UX as Campaigns filter.
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  const startEdit = (row) => {
    setEditing(row.id);
    setEditForm({
      prospect_name: row.prospect_name || "",
      prospect_firm: row.prospect_firm || "",
      prospect_email: row.prospect_email || "",
      prospect_phone: row.prospect_phone || "",
      internal_notes: row.internal_notes || "",
      sent_at: row.sent_at || "",
    });
  };

  const cancelEdit = () => { setEditing(null); setEditForm({}); };

  const submitEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.patch(`/admin/prospect-engagement/${editing}`, editForm);
      toast.success("Prospect metadata saved");
      cancelEdit();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const totals = useMemo(() => {
    const t = { views: 0, form_start: 0, complete: 0, primary: 0, phone: 0, unique: 0 };
    for (const r of rows) {
      const e = r.engagement || {};
      t.views += e.page_view || 0;
      t.form_start += e.form_start || 0;
      t.complete += e.form_complete_preview || 0;
      t.primary += e.cta_primary_click || 0;
      t.phone += e.cta_phone_click || 0;
      t.unique += e.unique_sessions || 0;
    }
    return t;
  }, [rows]);

  if (!isInternal) {
    return (
      <div className="brut-card p-8 text-center label-mono" data-testid="prospect-engagement-forbidden">
        Prospect engagement is an internal-only surface.
      </div>
    );
  }

  const total = pagination.total || 0;
  const start = total === 0 ? 0 : (pagination.page - 1) * pagination.page_size + 1;
  const end = (pagination.page - 1) * pagination.page_size + rows.length;

  return (
    <div data-testid="admin-prospect-engagement">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="label-mono">Sales · prospect visibility</div>
          <h1 className="font-chivo font-black text-4xl md:text-5xl uppercase tracking-tighter leading-none">
            Prospect engagement
          </h1>
          <div className="mt-1 text-sm text-zinc-600 max-w-3xl">
            Every preview link you mint from Landing Pages appears here.
            When a prospective buyer opens the page, opens the Thank-You,
            starts the form, or taps a CTA, the counters update in
            near-real-time. No PII is captured — only aggregate opens.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} data-testid="prospect-engagement-refresh"
            className="border border-black px-3 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:bg-black hover:text-white">
            <ArrowsClockwise size={12} weight="bold" /> Refresh
          </button>
        </div>
      </div>

      {/* KPI strip — page-scoped (accurate reflection of the current 25 rows) */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4" data-testid="prospect-engagement-kpis">
        <KpiCard label="Views" value={totals.views} testId="prospect-kpi-views" />
        <KpiCard label="Unique sessions" value={totals.unique} testId="prospect-kpi-unique" />
        <KpiCard label="Form starts" value={totals.form_start} testId="prospect-kpi-form-start" />
        <KpiCard label="Preview complete" value={totals.complete} testId="prospect-kpi-complete" />
        <KpiCard label="Primary CTA" value={totals.primary} testId="prospect-kpi-primary" />
        <KpiCard label="Phone taps" value={totals.phone} testId="prospect-kpi-phone" />
      </div>

      <div className="brut-card p-3 mb-3 flex flex-col md:flex-row md:items-center gap-2 md:flex-wrap"
           data-testid="prospect-engagement-toolbar">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input type="text" value={qInput} onChange={(e) => setQInput(e.target.value)}
            placeholder="Search prospect, firm, slug, or label"
            className="w-full border border-black pl-7 pr-2 py-1.5 text-sm"
            data-testid="prospect-engagement-search" />
        </div>
        <input type="text" value={firm} onChange={(e) => setFirm(e.target.value)}
          placeholder="Filter by firm"
          className="border border-black px-2 py-1.5 text-sm"
          data-testid="prospect-engagement-firm-filter" />
        <select value={viewed} onChange={(e) => setViewed(e.target.value)}
          data-testid="prospect-engagement-viewed-filter"
          className="border border-black px-2 py-1.5 text-sm">
          <option value="all">All shares</option>
          <option value="viewed">Viewed</option>
          <option value="not_viewed">Not viewed</option>
        </select>
        {(qInput || firm || viewed !== "all") && (
          <button onClick={() => { setQInput(""); setFirm(""); setViewed("all"); }}
            data-testid="prospect-engagement-clear"
            className="border border-black px-2 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1">
            <X size={12} /> Clear
          </button>
        )}
      </div>

      <div className="brut-card overflow-x-auto">
        <table className="w-full text-sm font-mono min-w-[1000px]" data-testid="prospect-engagement-table">
          <thead>
            <tr className="border-b border-black text-[10px] uppercase tracking-widest">
              <th className="py-2 px-3 text-left">Prospect</th>
              <th className="py-2 px-3 text-left">Firm</th>
              <th className="py-2 px-3 text-left">Slug</th>
              <th className="py-2 px-3 text-left">Views / Uniq</th>
              <th className="py-2 px-3 text-left">Form / Complete</th>
              <th className="py-2 px-3 text-left">CTAs (primary / phone)</th>
              <th className="py-2 px-3 text-left">First / Last</th>
              <th className="py-2 px-3 text-left">Sent</th>
              <th className="py-2 px-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const e = r.engagement || {};
              const lastVieweAt = e.last_engaged_at;
              const viewedFlag = Boolean(lastVieweAt);
              // Iter-63.1.7 — sample-page telemetry is intentionally
              // suppressed at the frontend beacon (see
              // `lib/prospectEngagement.js` `opts?.isSamplePage` guard).
              // Surface that explicitly so operators don't mistake a
              // sample-page row's zeroed counters for a broken tracker.
              const isSampleSlug = String(r.page_slug || r.slug || "")
                .toLowerCase().startsWith("sample-");
              return (
                <tr key={r.id} className={`border-b border-zinc-200 hover:bg-zinc-50 ${viewedFlag ? "" : "opacity-80"}`}
                    data-testid={`prospect-engagement-row-${r.id}`}>
                  <td className="py-2 px-3">
                    <div className="font-bold" data-testid={`prospect-engagement-name-${r.id}`}>
                      {r.prospect_name
                        ? r.prospect_name
                        : (r.label
                            ? <span className="italic text-zinc-600" title="Using the share label — click Edit to annotate a prospect name.">{r.label}</span>
                            : <span className="italic text-zinc-500">Untitled share</span>)}
                    </div>
                    {r.prospect_email && <div className="text-[10px] text-zinc-500">{r.prospect_email}</div>}
                    {r.prospect_phone && <div className="text-[10px] text-zinc-500">{r.prospect_phone}</div>}
                  </td>
                  <td className="py-2 px-3" data-testid={`prospect-engagement-firm-${r.id}`}>
                    {r.prospect_firm
                      ? r.prospect_firm
                      : (r.page_firm_name
                          ? <span className="italic text-zinc-600" title="Falling back to the landing page's firm name — click Edit to annotate a specific prospect firm.">
                              {r.page_firm_name}
                              <span className="not-italic text-[9px] uppercase tracking-widest text-zinc-400 ml-1">· from LP</span>
                            </span>
                          : <span className="text-zinc-400">—</span>)}
                  </td>
                  <td className="py-2 px-3 text-[11px]">
                    <a href={`/lp/${r.page_slug || r.slug}?preview=1`}
                       target="_blank" rel="noreferrer"
                       className="underline hover:text-emerald-700"
                       data-testid={`prospect-engagement-open-${r.id}`}>
                      /lp/{r.page_slug || r.slug || "?"}
                    </a>
                    {isSampleSlug && (
                      <div
                        data-testid={`prospect-engagement-sample-${r.id}`}
                        className="mt-1 inline-flex items-center gap-1 border border-amber-500 bg-amber-50 text-amber-900 px-1.5 py-0.5 text-[9px] uppercase tracking-widest"
                        title="Sample / demo pages intentionally do not emit prospect engagement events."
                      >
                        <span aria-hidden="true">◆</span>
                        Sample page — engagement not tracked
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <span data-testid={`prospect-engagement-views-${r.id}`}>{e.page_view || 0}</span>
                    <span className="text-zinc-400"> / {e.unique_sessions || 0}</span>
                  </td>
                  <td className="py-2 px-3">
                    <span data-testid={`prospect-engagement-form-${r.id}`}>{e.form_start || 0}</span>
                    <span className="text-zinc-400"> / </span>
                    <span data-testid={`prospect-engagement-complete-${r.id}`}>{e.form_complete_preview || 0}</span>
                  </td>
                  <td className="py-2 px-3">
                    <span data-testid={`prospect-engagement-primary-${r.id}`}>{e.cta_primary_click || 0}</span>
                    <span className="text-zinc-400"> / </span>
                    <span data-testid={`prospect-engagement-phone-${r.id}`}>{e.cta_phone_click || 0}</span>
                  </td>
                  <td className="py-2 px-3 text-[11px]">
                    <div>{e.first_engaged_at ? new Date(e.first_engaged_at).toLocaleString() : <span className="text-zinc-400">never</span>}</div>
                    <div className="text-zinc-500">{e.last_engaged_at ? new Date(e.last_engaged_at).toLocaleString() : ""}</div>
                  </td>
                  <td className="py-2 px-3 text-[11px]">{r.sent_at || <span className="text-zinc-400">—</span>}</td>
                  <td className="py-2 px-3 text-right">
                    <button onClick={() => startEdit(r)}
                      data-testid={`prospect-engagement-edit-${r.id}`}
                      className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1">
                      <PencilSimple size={11} weight="bold" /> Edit
                    </button>
                  </td>
                </tr>
              );
            })}
            {!loading && !rows.length && (
              <tr><td colSpan={9} className="py-8 text-center label-mono" data-testid="prospect-engagement-empty">
                No prospect shares match your filters. Mint one from Landing Pages → Share ALL previews.
              </td></tr>
            )}
            {loading && <tr><td colSpan={9} className="py-8 text-center label-mono">Loading…</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-xs font-mono uppercase tracking-widest text-zinc-700">
        <div data-testid="prospect-engagement-count">
          {total === 0 ? "No shares" : `Showing ${start}${start !== end ? `–${end}` : ""} of ${total}`}
        </div>
        <div className="flex items-center gap-2" data-testid="prospect-engagement-pagination">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}
            data-testid="prospect-engagement-page-prev"
            className="border border-black px-2 py-1 disabled:opacity-30 hover:bg-black hover:text-white">Prev</button>
          <span>Page {pagination.page || 1}</span>
          <button onClick={() => setPage(page + 1)} disabled={!pagination.has_next}
            data-testid="prospect-engagement-page-next"
            className="border border-black px-2 py-1 disabled:opacity-30 hover:bg-black hover:text-white">Next</button>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-40 flex items-start justify-center pt-16 bg-black/50"
             data-testid="prospect-engagement-modal">
          <div className="bg-white border-2 border-black brut-shadow-md max-w-lg w-full mx-4 p-5">
            <div className="flex items-center gap-2 mb-4">
              <ChartBar size={16} weight="bold" />
              <div className="font-chivo font-black uppercase tracking-tighter">Annotate prospect</div>
              <button onClick={cancelEdit} className="ml-auto label-mono hover:text-red-700"
                      data-testid="prospect-engagement-modal-close">
                Close ✕
              </button>
            </div>
            <div className="space-y-3">
              <Field label="Prospect name" testId="prospect-edit-name"
                     value={editForm.prospect_name}
                     onChange={(v) => setEditForm({ ...editForm, prospect_name: v })} />
              <Field label="Firm" testId="prospect-edit-firm"
                     value={editForm.prospect_firm}
                     onChange={(v) => setEditForm({ ...editForm, prospect_firm: v })} />
              <Field label="Email" testId="prospect-edit-email" type="email"
                     value={editForm.prospect_email}
                     onChange={(v) => setEditForm({ ...editForm, prospect_email: v })} />
              <Field label="Phone (optional)" testId="prospect-edit-phone"
                     value={editForm.prospect_phone}
                     onChange={(v) => setEditForm({ ...editForm, prospect_phone: v })} />
              <Field label="Sent date (ISO)" testId="prospect-edit-sent"
                     value={editForm.sent_at}
                     onChange={(v) => setEditForm({ ...editForm, sent_at: v })} />
              <div>
                <label className="label-mono">Internal notes</label>
                <textarea rows={3}
                  className="w-full border border-black px-2 py-1.5 mt-1 text-sm"
                  value={editForm.internal_notes}
                  onChange={(e) => setEditForm({ ...editForm, internal_notes: e.target.value })}
                  data-testid="prospect-edit-notes" />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={cancelEdit} disabled={saving}
                data-testid="prospect-engagement-modal-cancel"
                className="border border-black px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white">
                Cancel
              </button>
              <button onClick={submitEdit} disabled={saving}
                data-testid="prospect-engagement-modal-save"
                className="bg-black text-white border border-black px-3 py-1.5 text-[11px] uppercase tracking-widest disabled:opacity-40">
                {saving ? "Saving…" : "Save annotation"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, testId }) {
  return (
    <div className="brut-card p-3" data-testid={testId}>
      <div className="label-mono">{label}</div>
      <div className="font-chivo font-black text-2xl mt-1">{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, testId, type = "text" }) {
  return (
    <div>
      <label className="label-mono">{label}</label>
      <input type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-black px-2 py-1.5 mt-1 text-sm"
        data-testid={testId} />
    </div>
  );
}
